import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FolderKanban, CheckSquare, Clock, AlertCircle, Plus, FolderPlus } from 'lucide-react';
import { DashboardStats, PersonalTask, Task, Project, User } from '../types';
import { dashboardApi, personalTasksApi, usersApi } from '../services/api';
import { Header } from '../components/Layout/Header';
import { statusLabels } from '../components/common/Badge';
import { format, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Modal } from '../components/common/Modal';
import { ProjectForm } from '../components/Projects/ProjectForm';
import { TaskDetailPanel } from '../components/Tasks/TaskDetailPanel';
import { useProjectStore } from '../store/projectStore';
import { useTaskStore } from '../store/taskStore';
import { taskStatusColors } from '../constants/taskStatus';
import toast from 'react-hot-toast';
import { useWorkspaceStore } from '../store/workspaceStore';

// due_date is a date-only column (no time); parsing it with `new Date(string)` reads it as
// UTC midnight, which shifts a day back once rendered in any timezone behind UTC. Building
// the Date from its y/m/d parts keeps it anchored to local midnight instead.
function parseDateOnly(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [personalTasks, setPersonalTasks] = useState<PersonalTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectMembers, setProjectMembers] = useState<User[]>([]);

  const { projects, fetchProjects, createProject } = useProjectStore();
  const { createTask } = useTaskStore();
  const navigate = useNavigate();
  const activeWorkspaceId = useWorkspaceStore(state => state.activeWorkspaceId);

  useEffect(() => {
    dashboardApi.getStats(activeWorkspaceId || undefined)
      .then(res => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeWorkspaceId]);

  useEffect(() => {
    personalTasksApi.getAll()
      .then(res => setPersonalTasks(res.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [activeWorkspaceId, fetchProjects]);

  useEffect(() => {
    if (projects.length > 0 && !projects.some(project => project.id === selectedProjectId)) {
      setSelectedProjectId(projects[0].id);
    } else if (projects.length === 0) {
      setSelectedProjectId('');
    }
  }, [projects, selectedProjectId]);

  useEffect(() => {
    usersApi.getAll()
      .then(res => setProjectMembers(res.data))
      .catch(() => setProjectMembers([]));
  }, []);

  const handleCreateProject = async (data: Partial<Project>, memberIds?: string[]) => {
    try {
      const newProject = await createProject(data, memberIds);
      setIsProjectModalOpen(false);
      toast.success('Projeto criado!');
      navigate(`/projects/${newProject.id}`);
    } catch {
      toast.error('Falha ao criar projeto');
    }
  };

  const handleCreateTask = async (data: Partial<Task>) => {
    try {
      await createTask({ ...data, project_id: selectedProjectId });
      setIsTaskModalOpen(false);
      // Refresh stats
      dashboardApi.getStats(activeWorkspaceId || undefined).then(res => setStats(res.data)).catch(console.error);
      toast.success('Tarefa criada!');
    } catch {
      toast.error('Falha ao criar tarefa');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const taskChartData = stats ? [
    { status: 'todo' as const, name: statusLabels.todo, value: parseInt(stats.tasks.todo), color: taskStatusColors.todo },
    { status: 'in_progress' as const, name: statusLabels.in_progress, value: parseInt(stats.tasks.in_progress), color: taskStatusColors.in_progress },
    { status: 'in_review' as const, name: statusLabels.in_review, value: parseInt(stats.tasks.in_review), color: taskStatusColors.in_review },
    { status: 'done' as const, name: statusLabels.done, value: parseInt(stats.tasks.done), color: taskStatusColors.done },
  ] : [];
  const visibleTaskChartData = taskChartData.filter(item => item.value > 0);
  const taskTotal = taskChartData.reduce((sum, item) => sum + item.value, 0);
  const assigneeChartData = (stats?.tasksByAssignee || []).map(item => ({
    assigneeId: item.assignee_id,
    name: item.assignee_name,
    count: parseInt(item.count),
  }));
  const selectedProject = projects.find(project => project.id === selectedProjectId);
  const pendingPersonalTasks = personalTasks
    .filter(task => !task.is_done)
    .sort((a, b) => {
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return a.due_date.localeCompare(b.due_date);
    })
    .slice(0, 5);

  const openStatusTasks = (status: string) => {
    const workspaceFilter = activeWorkspaceId ? `&workspaceId=${encodeURIComponent(activeWorkspaceId)}` : '';
    navigate(`/tasks?status=${encodeURIComponent(status)}${workspaceFilter}`);
  };

  const openAssigneeTasks = (assigneeId: string, assigneeName: string) => {
    const workspaceFilter = activeWorkspaceId ? `&workspaceId=${encodeURIComponent(activeWorkspaceId)}` : '';
    navigate(`/tasks?assigneeId=${encodeURIComponent(assigneeId)}&assigneeName=${encodeURIComponent(assigneeName)}${workspaceFilter}`);
  };

  return (
    <div>
      <Header title="Painel" />
      <div className="p-6 space-y-6">
        {/* Quick Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsProjectModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors shadow-sm"
          >
            <FolderPlus size={16} />
            Criar Projeto
          </button>
          <button
            onClick={() => {
              if (projects.length === 0) {
                toast.error('Crie um projeto primeiro');
                return;
              }
              setIsTaskModalOpen(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors shadow-sm"
          >
            <Plus size={16} />
            Nova Tarefa
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total de Projetos"
            value={stats?.projects.total || '0'}
            icon={<FolderKanban size={24} className="text-primary-500" />}
            subtitle={`${stats?.projects.in_progress || 0} em andamento`}
            color="primary"
          />
          <StatCard
            title="Total de Tarefas"
            value={stats?.tasks.total || '0'}
            icon={<CheckSquare size={24} className="text-gray-600" />}
            subtitle={`${stats?.tasks.done || 0} concluídas`}
            color="gray"
          />
          <StatCard
            title="Em Andamento"
            value={stats?.tasks.in_progress || '0'}
            icon={<Clock size={24} className="text-primary-500" />}
            subtitle="Tarefas ativas"
            color="primary"
          />
          <StatCard
            title="Atrasadas"
            value={stats?.tasks.overdue || '0'}
            icon={<AlertCircle size={24} className="text-gray-700" />}
            subtitle="Precisam de atenção"
            color="gray"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* My Tasks */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Minhas Tarefas</h2>
              <Link to="/my-tasks" className="text-sm text-primary-600 hover:text-primary-700">Ver todas</Link>
            </div>
            <div className="space-y-3">
              {pendingPersonalTasks.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">Nenhuma tarefa pessoal pendente</p>
              )}
              {pendingPersonalTasks.map((task: PersonalTask) => {
                const isOverdue = !!task.due_date && isPast(parseDateOnly(task.due_date));
                return (
                  <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-1.5 h-8 rounded-full flex-shrink-0 bg-primary-500" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                        {task.description && (
                          <p className="text-xs text-gray-500 truncate">{task.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      {task.due_date && (
                        <span className={`text-xs ${isOverdue ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                        {format(parseDateOnly(task.due_date), 'd MMM', { locale: ptBR })}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Task Distribution Pie Chart */}
          <div className="rounded border border-[#D0D4E4] bg-white">
            <div className="flex items-center justify-between border-b border-[#D0D4E4] px-5 py-3">
              <h2 className="text-lg font-semibold text-[#323338]">Tarefas por status</h2>
              <span className="text-xl leading-none text-[#676879]">...</span>
            </div>
            <div className="grid min-h-[260px] grid-cols-1 items-center justify-items-center gap-4 p-5 xl:grid-cols-[minmax(160px,1fr)_auto] xl:justify-items-stretch">
              <div className="h-[220px] w-full min-w-[160px] max-w-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={visibleTaskChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius="82%"
                      dataKey="value"
                      onClick={(entry) => openStatusTasks((entry as unknown as { status: string }).status)}
                      cursor="pointer"
                    >
                      {visibleTaskChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} stroke={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [`${value} tarefa(s)`, name]}
                      contentStyle={{ borderColor: '#D0D4E4', borderRadius: 4 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid w-full gap-3 text-sm xl:min-w-44">
                {taskChartData.map(item => {
                  const percentage = taskTotal > 0 ? Math.round((item.value / taskTotal) * 1000) / 10 : 0;
                  return (
                    <button
                      key={item.status}
                      type="button"
                      onClick={() => openStatusTasks(item.status)}
                      className="flex items-center justify-between gap-3 rounded px-2 py-1 text-left transition hover:bg-[#F5F7FB]"
                    >
                      <span className="flex items-center gap-2 text-[#323338]">
                        <span className="h-3.5 w-3.5 rounded-full" style={{ backgroundColor: item.color }} />
                        {item.name}: {percentage}%
                      </span>
                      <span className="font-semibold text-[#323338]">{item.value}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Tasks by assignee Bar Chart */}
        <div className="rounded border border-[#D0D4E4] bg-white">
          <div className="flex items-center justify-between border-b border-[#D0D4E4] px-5 py-3">
            <h2 className="text-lg font-semibold text-[#323338]">Tarefas por atendente</h2>
            <span className="text-xl leading-none text-[#676879]">...</span>
          </div>
          <div className="p-5">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={assigneeChartData} margin={{ top: 14, right: 16, left: 0, bottom: 18 }}>
                <CartesianGrid vertical={false} stroke="#EEF1F8" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#323338' }} interval={0} />
                <YAxis tick={{ fontSize: 12, fill: '#676879' }} allowDecimals={false} />
                <Tooltip
                  formatter={(value) => [`${value} tarefa(s)`, 'Quantidade']}
                  contentStyle={{ borderColor: '#D0D4E4', borderRadius: 4 }}
                />
                <Bar
                  dataKey="count"
                  fill="#579BFC"
                  radius={[4, 4, 0, 0]}
                  cursor="pointer"
                  onClick={(entry) => {
                    const item = entry as unknown as { assigneeId: string; name: string };
                    openAssigneeTasks(item.assigneeId, item.name);
                  }}
                  label={{ position: 'top', fill: '#323338', fontSize: 13, fontWeight: 700 }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Create Project Modal */}
      <Modal isOpen={isProjectModalOpen} onClose={() => setIsProjectModalOpen(false)} title="Novo Projeto" size="lg">
        <ProjectForm onSubmit={handleCreateProject} onCancel={() => setIsProjectModalOpen(false)} />
      </Modal>

      {/* Create Task Modal */}
      <Modal isOpen={isTaskModalOpen} onClose={() => setIsTaskModalOpen(false)} title="" size="wide">
        <div className="space-y-4">
          <div className="px-6 pt-5 max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-1">Projeto *</label>
            <select
              value={selectedProjectId}
              onChange={e => setSelectedProjectId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          {selectedProjectId && (
            <TaskDetailPanel
              projectId={selectedProjectId}
              projectName={selectedProject?.name}
              members={projectMembers}
              onSubmit={handleCreateTask}
              onCancel={() => setIsTaskModalOpen(false)}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  subtitle: string;
  color: 'primary' | 'gray';
}

function StatCard({ title, value, icon, subtitle, color }: StatCardProps) {
  const colorMap = {
    primary: 'bg-primary-50 border-primary-100',
    gray: 'bg-white border-gray-200',
  };

  return (
    <div className={`${colorMap[color]} border rounded-xl p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        </div>
        {icon}
      </div>
    </div>
  );
}
